import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Download } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { statusLabel, levelLabel, levelBadgeClass, fmtDateOnly, noticeLine, isCardClosed, isNewChannelImpactCase, channelDisplayLabel, impactDisplayTags } from './conductUtils';

const LEVELS_ALL = ['LOGGED_WARNING', 'YELLOW', 'ORANGE', 'RED'];
const SEVERITIES = ['MINOR', 'MAJOR', 'GROSS'];

async function downloadFile(fileId, filename) {
  try {
    const res = await api.get(`/conduct/files/${fileId}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (e) {
    toast.error('Failed to download file');
  }
}

function PendingCaseRow({ c, categoryName, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [levelChoice, setLevelChoice] = useState(c.recommended_level || '');
  const [dismissReason, setDismissReason] = useState('');

  useEffect(() => { api.get(`/conduct/cases/${c.id}`).then((r) => setDetail(r.data)).catch(() => {}); }, [c.id]);

  const act = async (fn) => {
    try { await fn(); toast.success('Done'); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  return (
    <Card data-testid={`md-pending-case-${c.id}`}>
      <CardContent className="pt-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium" data-testid={`md-pending-employee-${c.id}`}>{c.employee_name || 'Unknown employee'}</span>
          <Badge variant="outline">{c.confirmed_severity}</Badge>
        </div>
        <p className="text-xs text-muted-foreground" data-testid={`md-pending-reported-by-${c.id}`}>
          Reported by {c.reporter_name || 'Unknown'}
          {detail?.reporting_manager_name ? ` · Reporting manager: ${detail.reporting_manager_name}` : ''}
        </p>
        <p className="text-sm">{categoryName(c.category)}: {c.what_happened}</p>
        {isNewChannelImpactCase(c) ? (
          <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid={`case-channel-impact-${c.id}`}>
            <Badge variant="outline">Channel: {channelDisplayLabel(c)}</Badge>
            {impactDisplayTags(c).map((t) => <Badge key={t} variant="secondary">{t}</Badge>)}
            {c.impact_usd != null && <Badge variant="outline">${Number(c.impact_usd).toLocaleString()}</Badge>}
            {c.impact_text && <span className="text-muted-foreground">{c.impact_text}</span>}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground" data-testid={`case-legacy-impact-${c.id}`}>
            Channel: {c.channel || '—'}{c.what_it_caused ? ` · ${c.what_it_caused}` : ''}{c.impact_text ? ` · ${c.impact_text}` : ''}
          </p>
        )}
        {c.recommended_level && (
          <p className="text-xs" data-testid={`md-pending-recommended-${c.id}`}>HR recommends: <span className="font-medium">{levelLabel(c.recommended_level)}</span></p>
        )}
        {!detail && <Loader2 className="h-4 w-4 animate-spin" />}
        {detail && (
          <>
            {detail.statement?.text ? (
              <div className="text-xs bg-muted/50 rounded p-2" data-testid={`md-pending-statement-${c.id}`}>
                <span className="font-medium">Employee's statement: </span>"{detail.statement.text}"
                {(detail.statement.file_ids || []).map((fid) => (
                  <Button key={fid} size="sm" variant="ghost" className="ml-1 h-6 px-2" data-testid={`md-pending-statement-file-${fid}`}
                    onClick={() => downloadFile(fid, `statement_${c.id}.bin`)}><Download className="h-3 w-3 mr-1" />File</Button>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic" data-testid={`md-pending-no-statement-${c.id}`}>
                {detail.deadlines?.statement_due_at ? `No statement, deadline passed (${fmtDateOnly(detail.deadlines.statement_due_at)})` : 'No statement submitted'}
              </p>
            )}
            {(detail.hr_call_notes || []).length > 0 && (
              <div className="text-xs text-muted-foreground" data-testid={`md-pending-call-notes-${c.id}`}>
                {detail.hr_call_notes.map((n, i) => <div key={i}>HR call note: "{n.text}"</div>)}
              </div>
            )}
            {(detail.recent_cards || []).length > 0 && (
              <div className="text-xs space-x-1" data-testid={`md-pending-recent-cards-${c.id}`}>
                <span className="font-medium">Cards in the last 12 months: </span>
                {detail.recent_cards.map((rc) => (
                  <Badge key={rc.id} variant="outline" className={levelBadgeClass(rc.level)}>
                    {levelLabel(rc.level)} ({fmtDateOnly(rc.issued_at)})
                  </Badge>
                ))}
              </div>
            )}
          </>
        )}
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <Select value={levelChoice} onValueChange={setLevelChoice}>
            <SelectTrigger className="w-44" data-testid={`md-level-select-${c.id}`}><SelectValue placeholder="Level" /></SelectTrigger>
            <SelectContent>{LEVELS_ALL.map((l) => <SelectItem key={l} value={l}>{levelLabel(l)}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" disabled={!levelChoice} data-testid={`md-approve-btn-${c.id}`}
            onClick={() => act(() => api.post(`/conduct/cases/${c.id}/md-decision`, { action: 'approve', level: levelChoice }))}>Approve &amp; Issue Card</Button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input className="h-8 text-xs w-56" placeholder="Reason to dismiss (required)" value={dismissReason}
            onChange={(e) => setDismissReason(e.target.value)} data-testid={`md-dismiss-reason-${c.id}`} />
          <Button size="sm" variant="destructive" disabled={!dismissReason.trim()} data-testid={`md-dismiss-btn-${c.id}`}
            onClick={() => act(() => api.post(`/conduct/cases/${c.id}/md-decision`, { action: 'dismiss', reason: dismissReason }))}>Dismiss</Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function MDApprovalsTab() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [levelChoice, setLevelChoice] = useState({});
  const [severityChoice, setSeverityChoice] = useState({});
  const [reasons, setReasons] = useState({});
  const [appealLevelChoice, setAppealLevelChoice] = useState({});
  const [notes, setNotes] = useState({});
  const [conflictDismissReasons, setConflictDismissReasons] = useState({});
  const [reviewerChoice, setReviewerChoice] = useState({});
  const [managers, setManagers] = useState([]);
  const [sendBackReasons, setSendBackReasons] = useState({});

  const categoryName = (id) => categories.find((cat) => cat.id === id)?.name || id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/conduct/md/approvals');
      setData(res.data);
    } catch (e) { toast.error('Failed to load MD approvals'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/conduct/hr/managers').then((r) => setManagers(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get('/conduct/categories').then((r) => setCategories(r.data)).catch(() => {}); }, []);

  const act = async (fn) => {
    try { await fn(); toast.success('Done'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  const addCallNote = async (caseId) => {
    try {
      await api.post(`/conduct/cases/${caseId}/call-notes`, { text: notes[caseId] || '' });
      toast.success('Call note added');
      setNotes({ ...notes, [caseId]: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  if (loading || !data) return <div className="flex justify-center py-12" data-testid="md-approvals-loading"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="md-approvals-tab">
      <div>
        <h3 className="font-semibold mb-2">Pending Approvals ({data.pending_cases.length})</h3>
        <div className="space-y-2">
          {data.pending_cases.map((c) => <PendingCaseRow key={c.id} c={c} categoryName={categoryName} onChanged={load} />)}
          {data.pending_cases.length === 0 && <p className="text-sm text-muted-foreground" data-testid="md-pending-empty">Nothing pending.</p>}
        </div>
      </div>

      {data.conflict_cases.length > 0 && (
        <div>
          <h3 className="font-semibold mb-2">HR-Involved Cases (handled directly by you)</h3>
          <div className="space-y-2">
            {data.conflict_cases.map((c) => (
              <Card key={c.id} data-testid={`md-conflict-case-${c.id}`}>
                <CardContent className="pt-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium" data-testid={`md-conflict-case-employee-${c.id}`}>{c.employee_name}</span>
                    <Badge variant="outline">{statusLabel(c.status, c, 'md')}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{categoryName(c.category)} ({c.proposed_severity}) — {c.what_happened}</p>
                  {isNewChannelImpactCase(c) ? (
                    <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid={`case-channel-impact-${c.id}`}>
                      <Badge variant="outline">Channel: {channelDisplayLabel(c)}</Badge>
                      {impactDisplayTags(c).map((t) => <Badge key={t} variant="secondary">{t}</Badge>)}
                      {c.impact_usd != null && <Badge variant="outline">${Number(c.impact_usd).toLocaleString()}</Badge>}
                      {c.impact_text && <span className="text-muted-foreground">{c.impact_text}</span>}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground" data-testid={`case-legacy-impact-${c.id}`}>
                      Channel: {c.channel || '—'}{c.what_it_caused ? ` · ${c.what_it_caused}` : ''}{c.impact_text ? ` · ${c.impact_text}` : ''}
                    </p>
                  )}
                  {c.statement?.text && (
                    <div className="text-xs bg-muted/50 rounded p-2" data-testid={`md-conflict-statement-${c.id}`}>
                      <span className="font-medium">Employee's statement: </span>"{c.statement.text}"
                      {(c.statement.file_ids || []).map((fid) => (
                        <Button key={fid} size="sm" variant="ghost" className="ml-1 h-6 px-2" data-testid={`md-conflict-statement-file-${fid}`}
                          onClick={() => downloadFile(fid, `statement_${c.id}.bin`)}><Download className="h-3 w-3 mr-1" />File</Button>
                      ))}
                    </div>
                  )}
                  {(c.hr_call_notes || []).length > 0 && (
                    <div className="text-xs text-muted-foreground" data-testid={`md-conflict-call-notes-${c.id}`}>
                      {c.hr_call_notes.map((n, i) => <div key={i}>Call note: "{n.text}"</div>)}
                    </div>
                  )}

                  {c.status === 'HR_REVIEW' && (
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" data-testid={`md-conflict-proceed-btn-${c.id}`}
                        onClick={() => act(() => api.post(`/conduct/cases/${c.id}/proceed`))}>Proceed (request statement)</Button>
                      <Button size="sm" variant="outline" data-testid={`md-conflict-request-info-btn-${c.id}`}
                        onClick={() => act(() => api.post(`/conduct/cases/${c.id}/request-info`, { text: notes[c.id] || 'Please provide more detail' }))}>Request Info</Button>
                      <Input className="h-8 text-xs w-56" placeholder="Reason to dismiss (required)" value={conflictDismissReasons[c.id] || ''}
                        onChange={(e) => setConflictDismissReasons({ ...conflictDismissReasons, [c.id]: e.target.value })} data-testid={`md-conflict-dismiss-reason-${c.id}`} />
                      <Button size="sm" variant="destructive" disabled={!(conflictDismissReasons[c.id] || '').trim()} data-testid={`md-conflict-dismiss-btn-${c.id}`}
                        onClick={() => act(() => api.post(`/conduct/cases/${c.id}/dismiss`, { reason: conflictDismissReasons[c.id] }))}>Dismiss</Button>
                    </div>
                  )}

                  {c.status === 'INFO_REQUESTED' && (
                    <p className="text-xs text-muted-foreground" data-testid={`md-conflict-waiting-info-${c.id}`}>Waiting for the reporter's reply to your information request.</p>
                  )}
                  {c.status === 'AWAITING_STATEMENT' && (
                    <p className="text-xs text-muted-foreground" data-testid={`md-conflict-waiting-statement-${c.id}`}>Waiting for the employee's statement.</p>
                  )}

                  {c.status === 'HR_DECISION' && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Select value={severityChoice[c.id] || ''} onValueChange={(v) => setSeverityChoice({ ...severityChoice, [c.id]: v })}>
                        <SelectTrigger className="w-32" data-testid={`md-conflict-severity-${c.id}`}><SelectValue placeholder="Severity" /></SelectTrigger>
                        <SelectContent>{SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={levelChoice[c.id] || ''} onValueChange={(v) => setLevelChoice({ ...levelChoice, [c.id]: v })}>
                        <SelectTrigger className="w-44" data-testid={`md-conflict-level-${c.id}`}><SelectValue placeholder="Level" /></SelectTrigger>
                        <SelectContent>{LEVELS_ALL.map((l) => <SelectItem key={l} value={l}>{levelLabel(l)}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button size="sm" disabled={!severityChoice[c.id] || !levelChoice[c.id]} data-testid={`md-conflict-approve-btn-${c.id}`}
                        onClick={() => act(() => api.post(`/conduct/cases/${c.id}/md-decision`, { action: 'approve', confirmed_severity: severityChoice[c.id], level: levelChoice[c.id] }))}>Approve &amp; Issue Card</Button>
                      <Input className="h-8 text-xs w-56" placeholder="Reason to dismiss (required)" value={conflictDismissReasons[c.id] || ''}
                        onChange={(e) => setConflictDismissReasons({ ...conflictDismissReasons, [c.id]: e.target.value })} data-testid={`md-conflict-decision-dismiss-reason-${c.id}`} />
                      <Button size="sm" variant="destructive" disabled={!(conflictDismissReasons[c.id] || '').trim()} data-testid={`md-conflict-decision-dismiss-btn-${c.id}`}
                        onClick={() => act(() => api.post(`/conduct/cases/${c.id}/md-decision`, { action: 'dismiss', reason: conflictDismissReasons[c.id] }))}>Dismiss</Button>
                    </div>
                  )}

                  <div className="flex gap-2 items-start">
                    <Textarea className="text-xs" placeholder="Note / info request text" data-testid={`md-conflict-note-input-${c.id}`}
                      value={notes[c.id] || ''} onChange={(e) => setNotes({ ...notes, [c.id]: e.target.value })} />
                    <Button size="sm" variant="secondary" data-testid={`md-conflict-call-note-btn-${c.id}`}
                      onClick={() => addCallNote(c.id)}>Add Call Note</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {data.conflict_cards.length > 0 && (
        <div>
          <h3 className="font-semibold mb-2">HR-Involved Cards (handled directly by you)</h3>
          <div className="space-y-2">
            {data.conflict_cards.map((card) => (
              <Card key={card.id} data-testid={`md-conflict-card-${card.id}`}>
                <CardContent className="pt-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{card.employee_name || 'Unknown employee'}</span>
                    <Badge className={levelBadgeClass(card.level)} variant="outline">{levelLabel(card.level)}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{statusLabel(card.status, card, 'hr')}</p>
                  {noticeLine(card, 'staff') && <p className="text-xs text-amber-700">{noticeLine(card, 'staff')}</p>}
                  {!isCardClosed(card.status) && (
                  <div className="flex flex-wrap gap-2">
                    {card.cap && card.cap.actions?.length > 0 && !card.cap.approved_by && (
                      <Button size="sm" variant="outline" data-testid={`md-conflict-approve-cap-btn-${card.id}`}
                        onClick={() => act(() => api.post(`/conduct/caps/${card.cap.id}/approve`))}>Approve CAP</Button>
                    )}
                    {card.cap && card.cap.approved_by && !(card.cap.reviews?.length) && (
                      <div className="flex gap-2 items-center">
                        <Input className="h-8 text-xs w-48" placeholder="Reason to send CAP back"
                          value={sendBackReasons[card.id] || ''} onChange={(e) => setSendBackReasons({ ...sendBackReasons, [card.id]: e.target.value })}
                          data-testid={`md-conflict-send-back-reason-input-${card.id}`} />
                        <Button size="sm" variant="outline" disabled={!(sendBackReasons[card.id] || '').trim()} data-testid={`md-conflict-send-back-cap-btn-${card.id}`}
                          onClick={() => act(() => api.post(`/conduct/caps/${card.cap.id}/send-back`, { reason: sendBackReasons[card.id] })).then(() => setSendBackReasons({ ...sendBackReasons, [card.id]: '' }))}>
                          Send Back CAP
                        </Button>
                      </div>
                    )}
                    {card.cap && card.cap.accepted_at && !card.reviewer_id && (
                      <div className="flex gap-2 items-center">
                        <Select value={reviewerChoice[card.id] || ''} onValueChange={(v) => setReviewerChoice({ ...reviewerChoice, [card.id]: v })}>
                          <SelectTrigger className="w-44" data-testid={`md-conflict-reviewer-select-${card.id}`}><SelectValue placeholder="Assign reviewer" /></SelectTrigger>
                          <SelectContent>{managers.filter((m) => m.id !== card.employee_id && m.id !== card.reporter_id && m.id !== user?.id).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button size="sm" disabled={!reviewerChoice[card.id]} data-testid={`md-conflict-assign-reviewer-btn-${card.id}`}
                          onClick={() => act(() => api.post(`/conduct/cards/${card.id}/assign-reviewer`, { reviewer_id: reviewerChoice[card.id] }))}>Assign</Button>
                      </div>
                    )}
                    {card.cap?.reviews?.some((r) => r.cap_met === true) && !card.cap.hr_verified_at && (
                      <Button size="sm" variant="outline" data-testid={`md-conflict-verify-cap-btn-${card.id}`}
                        onClick={() => act(() => api.post(`/conduct/caps/${card.cap.id}/verify`))}>Verify CAP Met</Button>
                    )}
                    {(card.level === 'ORANGE' || card.level === 'YELLOW') && !card.letter_file_id && (
                      <Button size="sm" variant="outline" data-testid={`md-conflict-generate-letter-btn-${card.id}`}
                        onClick={() => act(() => api.post(`/conduct/cards/${card.id}/letter`, {}))}>Generate Warning Letter</Button>
                    )}
                    {card.letter_file_id && (
                      <Button size="sm" variant="ghost" data-testid={`md-conflict-download-letter-btn-${card.id}`}
                        onClick={() => downloadFile(card.letter_file_id, `warning_letter_${card.id}.pdf`)}>
                        <Download className="h-3.5 w-3.5 mr-1" />Download Letter
                      </Button>
                    )}
                    {card.level === 'RED' && !card.show_cause?.file_id && (
                      <Button size="sm" variant="outline" data-testid={`md-conflict-send-show-cause-btn-${card.id}`}
                        onClick={() => act(() => api.post(`/conduct/cards/${card.id}/show-cause`))}>Send Show-Cause Notice</Button>
                    )}
                    {card.level === 'RED' && card.show_cause?.file_id && (
                      <Button size="sm" variant="ghost" data-testid={`md-conflict-download-show-cause-btn-${card.id}`}
                        onClick={() => downloadFile(card.show_cause.file_id, `show_cause_${card.id}.pdf`)}>
                        <Download className="h-3.5 w-3.5 mr-1" />Download Notice
                      </Button>
                    )}
                  </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}


      {data.red_cards.length > 0 && (
        <div>
          <h3 className="font-semibold mb-2">Red Cards — Show-Cause Decisions</h3>
          <div className="space-y-2">
            {data.red_cards.map((card) => {
              const sent = !!card.show_cause?.sent_at;
              const deadlinePassed = sent && card.show_cause?.reply_due_at && new Date(card.show_cause.reply_due_at) < new Date();
              const readyForReply = sent && (!!card.show_cause?.reply || deadlinePassed);
              const appealWindowOpen = card.appeal_due_at && new Date(card.appeal_due_at) > new Date();
              const appealBlockReason = card.under_appeal ? 'Appeal pending' : (appealWindowOpen ? `Waiting for the appeal window until ${fmtDateOnly(card.appeal_due_at)}` : null);
              const canDecide = readyForReply && !appealBlockReason;
              return (
                <Card key={card.id} data-testid={`md-red-card-${card.id}`}>
                  <CardContent className="pt-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium" data-testid={`red-card-employee-${card.id}`}>{card.employee_name}</span>
                      {!sent && <Badge variant="outline" data-testid={`red-card-not-sent-${card.id}`}>Show-cause not sent yet</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground" data-testid={`red-card-what-for-${card.id}`}>{categoryName(card.category)}: {card.what_happened}</p>
                    {sent && <p className="text-sm">Reply due {fmtDateOnly(card.show_cause?.reply_due_at)}</p>}
                    {card.show_cause?.reply && <p className="text-xs text-muted-foreground italic">"{card.show_cause.reply}"</p>}
                    {sent && !readyForReply && (
                      <p className="text-xs text-muted-foreground" data-testid={`red-card-waiting-${card.id}`}>Waiting on the employee's reply or the deadline to pass before you can decide.</p>
                    )}
                    {appealBlockReason && (
                      <p className="text-xs text-amber-700" data-testid={`red-card-appeal-block-${card.id}`}>{appealBlockReason}</p>
                    )}
                    {noticeLine(card, 'staff') && <p className="text-xs text-amber-700" data-testid={`red-card-notice-${card.id}`}>{noticeLine(card, 'staff')}</p>}
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" disabled={!canDecide} data-testid={`red-revert-btn-${card.id}`}
                        onClick={() => act(() => api.post(`/conduct/cards/${card.id}/red-decision`, { decision: 'REVERT_TO_ORANGE' }))}>Revert to Orange</Button>
                      <Button size="sm" variant="destructive" disabled={!canDecide} data-testid={`red-exit-btn-${card.id}`}
                        onClick={() => {
                          if (window.confirm(`Exit is irreversible and will mark ${card.employee_name}'s notice period as executed. Continue?`)) {
                            act(() => api.post(`/conduct/cards/${card.id}/red-decision`, { decision: 'EXIT' }));
                          }
                        }}>Exit</Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {data.pending_appeals.length > 0 && (
        <div>
          <h3 className="font-semibold mb-2">Appeals</h3>
          <div className="space-y-2">
            {data.pending_appeals.map((a) => (
              <Card key={a.id} data-testid={`md-appeal-${a.id}`}>
                <CardContent className="pt-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium" data-testid={`md-appeal-employee-${a.id}`}>{a.employee_name}</span>
                    <Badge className={levelBadgeClass(a.card_level)} variant="outline">{levelLabel(a.card_level)}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground" data-testid={`md-appeal-card-info-${a.id}`}>
                    {categoryName(a.category)}: {a.what_happened} — issued {fmtDateOnly(a.card_issued_at)}
                  </p>
                  <p className="text-sm italic">"{a.grounds}"</p>
                  {(a.file_ids || []).map((fid) => (
                    <Button key={fid} size="sm" variant="ghost" className="h-6 px-2" data-testid={`md-appeal-file-${fid}`}
                      onClick={() => downloadFile(fid, `appeal_${a.id}.bin`)}><Download className="h-3 w-3 mr-1" />Evidence file</Button>
                  ))}
                  <Textarea
                    className="text-xs"
                    placeholder="Reason for your decision (required)"
                    data-testid={`appeal-reason-input-${a.id}`}
                    value={reasons[a.id] || ''}
                    onChange={(e) => setReasons({ ...reasons, [a.id]: e.target.value })}
                  />
                  <div className="flex gap-2 items-center flex-wrap">
                    <Button size="sm" variant="outline" disabled={!(reasons[a.id] || '').trim()} data-testid={`appeal-uphold-btn-${a.id}`}
                      onClick={() => act(() => api.post(`/conduct/appeals/${a.id}/decide`, { decision: 'UPHELD', reason: reasons[a.id] }))}>Uphold</Button>
                    <Button size="sm" variant="outline" disabled={!(reasons[a.id] || '').trim()} data-testid={`appeal-cancel-btn-${a.id}`}
                      onClick={() => act(() => api.post(`/conduct/appeals/${a.id}/decide`, { decision: 'CANCELLED', reason: reasons[a.id] }))}>Cancel Card</Button>
                    <Select value={appealLevelChoice[a.id] || ''} onValueChange={(v) => setAppealLevelChoice({ ...appealLevelChoice, [a.id]: v })}>
                      <SelectTrigger className="w-40" data-testid={`appeal-reduce-level-${a.id}`}><SelectValue placeholder="Reduce to..." /></SelectTrigger>
                      <SelectContent>{LEVELS_ALL.slice(0, LEVELS_ALL.indexOf(a.card_level)).map((l) => <SelectItem key={l} value={l}>{levelLabel(l)}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" disabled={!(reasons[a.id] || '').trim() || !appealLevelChoice[a.id]} data-testid={`appeal-reduce-btn-${a.id}`}
                      onClick={() => act(() => api.post(`/conduct/appeals/${a.id}/decide`, { decision: 'REDUCED', new_level: appealLevelChoice[a.id], reason: reasons[a.id] }))}>Reduce</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
